import ErrorModal from '@/components/ErrorModal.tsx';
import StaleEnrollmentModal from '@/components/StaleEnrollmentModal.tsx';
import {useStaleEnrollmentCheck} from '@/hooks/useStaleEnrollmentCheck.ts';
import {normalizeAvailability} from '@/shared/availability.ts';
import {
    StudentEnrollmentForms,
    getNextStudentEnrollmentFormKey,
    getPreviousStudentEnrollmentFormKey,
} from '@/shared/enrollment.keys.ts';
import {isGuardianEditableStatus} from '@/shared/enrollmentStatus.ts';
import type {DeleteDocumentResponse, Document, Enrollment} from '@/shared/model.interface.ts';
import {useNavigate, useParams} from '@tanstack/react-router';
import axios from 'axios';
import moment from 'moment';
import type * as React from 'react';
import {
    type RefObject,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {type ZodTypeAny, z} from 'zod';
import {
    enrollmentQueryKey,
    fetchEnrollment,
    saveEnrollment,
    storedAdminHeaders,
    submitEnrollment,
} from '../../hooks/enrollment';
import useFormSchema, {type FormSchemas, type RefinedForms} from '../../hooks/useFormSchema';
import {type JWTUser, getStoredAdminUser} from './JWTProvider';

type MutateError = {
    message: string;
};

export type FormContextInterface = {
    enrollment: Enrollment | undefined;
    formRef: RefObject<UseFormReturn | null>;
    userStep?: number | undefined;
    setUserStep: (userStep?: number) => void;
    handleFormSave: (values: Record<string, unknown>, nextAction?: string) => void;
    formSchema: RefinedForms;
    enrollmentSchema: any;
    removeAttachmentFromParentForm: (documentId: string, step: string) => Promise<void>;
    validatePage: (formKey: number, enrollment: Enrollment) => Promise<boolean>;
    validateEnrollment: (enrollment: Enrollment) => Promise<boolean>;
    isProcessing: boolean;
    isSaving: boolean;
    isSubmitting: boolean;
    isStale: boolean;
};

const FormControlContext = createContext<FormContextInterface>({} as FormContextInterface);

type FormControlContextProviderProps = {
    authenticatedUser?: JWTUser | null;
    children: React.ReactNode;
};

export const formContinue = 'continue';
export const formPrevious = 'previous';
export const formSave = 'save';
export const formSubmit = 'submit';
export const formClose = 'close';

const FormControlContextProvider = ({
    authenticatedUser: authenticatedUserProp,
    children,
}: FormControlContextProviderProps): React.ReactElement => {
    // admins open the form in its own tab, outside JWTProvider
    const [storedAdminUser] = useState(getStoredAdminUser);
    const authenticatedUser = authenticatedUserProp ?? storedAdminUser;
    const formRef = useRef<UseFormReturn | null>(null);
    const {t} = useTranslation();
    const {getFormSchema} = useFormSchema();
    const {enrollmentId, step} = useParams({strict: false});
    const startingStep = step !== undefined ? Number.parseInt(step) : step;
    const [userStep, setUserStep] = useState<number | undefined>(startingStep);
    const [hasError, setHasError] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [leaveOnErrorClose, setLeaveOnErrorClose] = useState(false);
    const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // get form schema
    const formSchema = getFormSchema(userStep === undefined ? 100 : userStep);
    const enrollmentSchema = getFormSchema();

    // queries/mutations
    const {mutate: deleteDocument} = useMutation(async (documentId: string) =>
        axios.post(
            `${import.meta.env.VITE_REACT_APP_API_ENDPOINT}/v1/document/delete`,
            {documentId: documentId},
            {headers: storedAdminHeaders()},
        ),
    );
    const {
        isLoading: isEnrollmentLoading,
        isError: isEnrollmentError,
        data: enrollment,
        error: enrollmentError,
    } = useQuery(
        enrollmentQueryKey(enrollmentId ?? ''),
        async () => fetchEnrollment(enrollmentId ?? ''),
        {
            // the form must start from, and save with, the same copy; no refetch under it
            cacheTime: 0,
            refetchOnReconnect: false,
            onSuccess: enrollment => {
                // hydrate for non-trivial types
                enrollment.dob = enrollment.dob ? moment(enrollment.dob) : null;
                enrollment.entryDate = moment(enrollment.entryDate);
                enrollment.leaseExpires = moment(enrollment.leaseExpires);
                enrollment.noPermanentResidenceDate = moment(enrollment.noPermanentResidenceDate);
                enrollment.signatureDate = moment(enrollment.signatureDate);

                if (
                    enrollment.status === 'Under Review' ||
                    enrollment.status === 'Ready for Import'
                ) {
                    navigate({to: `/dashboard/${enrollment.guardianId}`});
                }

                // guardians can only edit In-Progress and Awaiting Documents
                if (authenticatedUser === null && !isGuardianEditableStatus(enrollment.status)) {
                    navigate({to: `/dashboard/${enrollment.guardianId}`});
                }

                if (userStep === undefined) {
                    setUserStep(enrollment.step);
                }
            },
        },
    );

    const {mutate: updateEnrollment, isLoading: isSaving} = useMutation(saveEnrollment);
    const {mutate: submittedEnrollment, isLoading: isSubmitting} = useMutation(submitEnrollment);
    const {isStale, serverUpdatedAt, markStale, clearStale} = useStaleEnrollmentCheck(
        enrollmentId,
        enrollment?.updatedAt,
        isSaving || isSubmitting,
    );

    const keepThisTabsVersion = () => {
        // adopt the server version so this tab's next save overwrites the newer data
        queryClient.setQueryData<Enrollment | undefined>(
            enrollmentQueryKey(enrollmentId ?? ''),
            current =>
                current && serverUpdatedAt ? {...current, updatedAt: serverUpdatedAt} : current,
        );
        clearStale();
    };

    const handleSaveError = useCallback(
        (error: unknown) => {
            const hint =
                axios.isAxiosError(error) && error.response?.status === 409
                    ? (error.response.data as {hint?: string} | undefined)?.hint
                    : undefined;

            if (hint === 'enrollment-stale' && enrollmentId) {
                fetchEnrollment(enrollmentId)
                    .then(serverEnrollment => markStale(serverEnrollment.updatedAt))
                    .catch(() => {
                        setErrorMessage('saveError');
                        setHasError(true);
                    });
                return;
            }

            if (hint === 'enrollment-locked') {
                // only a tab that started as admin can have lost its admin session
                const adminExpired = authenticatedUser !== null && getStoredAdminUser() === null;
                setErrorMessage(adminExpired ? 'adminSessionExpired' : 'enrollmentLocked');
                setLeaveOnErrorClose(!adminExpired);
                setHasError(true);
                return;
            }

            console.error('validation error', error);
            setErrorMessage('saveError');
            setHasError(true);
        },
        [enrollmentId, markStale, authenticatedUser],
    );

    const castWithoutValidation = (
        fullSchema: RefinedForms,
        data: {[s: string]: unknown} | ArrayLike<unknown>,
    ) => {
        let schema: FormSchemas | ZodTypeAny = fullSchema;
        while (schema instanceof z.ZodEffects) {
            schema = schema._def.schema;
        }

        if (!(schema instanceof z.ZodObject)) {
            throw new Error('Schema must be a Zod object schema');
        }

        const shape = schema.shape;

        return Object.fromEntries(
            Object.entries(data).map(([key, value]) => {
                if (!(key in shape)) {
                    return [key, value]; // Keep extra fields unchanged
                }

                try {
                    return [key, shape[key].parse(value)]; // Apply transformation without full validation
                } catch {
                    return [key, value]; // Keep original value if parsing fails
                }
            }),
        );
    };

    const validateEnrollment = async (enrollment: Enrollment) => {
        let isValid = true;

        for (const formKey of StudentEnrollmentForms) {
            const pageIsValid = await validatePage(formKey, enrollment);

            if (!pageIsValid) {
                isValid = false;
            }
        }

        return isValid;
    };

    const validatePage = async (formKey: number, enrollment: Enrollment) => {
        let isValid = true;

        if (enrollment) {
            const formKeySchema = getFormSchema(formKey);
            // The page schemas expect each step's documents under documents205/400/501,
            // but the enrollment stores them in a single flat list. Hydrate the per-step
            // arrays from that list so every page validates against its own documents
            // regardless of which page was saved last.
            const enrollmentToValidate = {
                ...enrollment,
                documents205:
                    enrollment.documents?.filter(document => document.step === '205') ?? [],
                documents400:
                    enrollment.documents?.filter(document => document.step === '400') ?? [],
                documents501:
                    enrollment.documents?.filter(document => document.step === '501') ?? [],
            };
            const result = formKeySchema?.safeParse(enrollmentToValidate);
            isValid = result.success;

            if (!result.success) {
                console.debug(`[validatePage] step ${formKey} is invalid`, result.error.issues);
            }
        }

        return isValid;
    };

    const handleFormSave = useCallback(
        async (rawValues: Record<string, unknown>, submitType?: string) => {
            // this tab's data is out of date, nothing saves until the user reloads or keeps theirs
            if (isStale) {
                return;
            }

            // @todo: find a better fix
            if (rawValues.unableToProvideDocumentsSituations === '') {
                rawValues.unableToProvideDocumentsSituations = [];
            }

            const result = formSchema?.safeParse(rawValues);
            const values = result.success
                ? result.data
                : castWithoutValidation(formSchema, rawValues);

            if (enrollment !== undefined && userStep !== undefined) {
                const nextstep = getNextStudentEnrollmentFormKey(userStep);
                const prevstep = getPreviousStudentEnrollmentFormKey(userStep);

                // Documents are edited one page at a time (documents205/400/501) but the
                // enrollment stores them as a single flat list. Start from the existing
                // documents and replace only the step(s) present on this page, so saving
                // one page never drops the documents that belong to the other pages.
                const documentStepKeys = ['205', '400', '501'];
                const valuesRecord = values as Record<string, unknown>;
                let documents: Document[] = [...(enrollment.documents ?? [])];
                for (const documentStep of documentStepKeys) {
                    const pageDocuments = valuesRecord[`documents${documentStep}`];
                    if (Array.isArray(pageDocuments)) {
                        documents = documents.filter(document => document.step !== documentStep);
                        documents = [...documents, ...(pageDocuments as Document[])];
                    }
                }

                const newEnrollment = {
                    ...enrollment,
                    ...values,
                    documents: documents,
                    // form values hold the updatedAt from when the page mounted, the cache is current
                    updatedAt: enrollment.updatedAt,
                };

                newEnrollment.documents205 = undefined;
                newEnrollment.documents400 = undefined;
                newEnrollment.documents501 = undefined;

                const toAvailabilityString = (value: unknown): string | null => {
                    const normalized = normalizeAvailability(value);
                    return normalized === null ? null : normalized ? 'true' : 'false';
                };
                newEnrollment.birthCertificateAvailability = toAvailabilityString(
                    newEnrollment.birthCertificateAvailability,
                );
                newEnrollment.parentIdAvailability = toAvailabilityString(
                    newEnrollment.parentIdAvailability,
                );

                if (
                    submitType === formContinue &&
                    nextstep !== undefined &&
                    nextstep > enrollment.step
                ) {
                    newEnrollment.step = nextstep;
                }

                if (submitType === formSubmit) {
                    // admins also get the documents check, unless staff already moved it further along
                    if (authenticatedUser === null || isGuardianEditableStatus(enrollment.status)) {
                        const outstandingDocuments = newEnrollment.documents.filter(
                            (document: Document) => document.willDropOffOrMailIn,
                        );

                        if (outstandingDocuments?.length) {
                            newEnrollment.status = 'Submitted: Awaiting Documents';
                        } else {
                            newEnrollment.status = 'Submitted';
                        }
                    }

                    if (authenticatedUser === null) {
                        newEnrollment.dateSubmitted = new Date();
                    }
                }

                // One save at a time: overlapping saves would send the same updatedAt and the
                // second would be rejected as stale by this tab's own first save.
                const enrollmentKey = enrollmentQueryKey(enrollment.id);
                const previousSave = saveQueueRef.current;
                saveQueueRef.current = previousSave.then(
                    () =>
                        new Promise<void>(resolve => {
                            const latestUpdatedAt =
                                queryClient.getQueryData<Enrollment>(enrollmentKey)?.updatedAt ??
                                newEnrollment.updatedAt;

                            updateEnrollment(
                                {...newEnrollment, updatedAt: latestUpdatedAt} as Enrollment,
                                {
                                    onSettled: () => resolve(),
                                    onSuccess: (returnedEnrollment: Enrollment) => {
                                        if ([205, 400, 501].includes(userStep)) {
                                            const currentPageDocuments =
                                                returnedEnrollment.documents?.filter(
                                                    (document: Document) =>
                                                        document.step === userStep.toString(),
                                                );

                                            // @ts-expect-error it's going to be there don't worry about it
                                            values[`documents${userStep}`] = currentPageDocuments;

                                            if (userStep === 205) {
                                                returnedEnrollment.documents205 =
                                                    currentPageDocuments;
                                            }

                                            if (userStep === 400) {
                                                returnedEnrollment.documents400 =
                                                    currentPageDocuments;
                                            }

                                            if (userStep === 501) {
                                                returnedEnrollment.documents501 =
                                                    currentPageDocuments;
                                            }
                                        }

                                        // update the cache
                                        queryClient.setQueryData(
                                            enrollmentQueryKey(enrollment.id),
                                            returnedEnrollment as Enrollment,
                                        );

                                        // navigate
                                        if (submitType === formContinue && nextstep !== undefined) {
                                            setUserStep(nextstep);
                                        } else if (
                                            submitType === formPrevious &&
                                            prevstep !== undefined
                                        ) {
                                            setUserStep(prevstep);
                                        } else if (
                                            submitType === formClose &&
                                            enrollment &&
                                            enrollment.guardianId !== undefined
                                        ) {
                                            if (authenticatedUser !== null) {
                                                navigate({to: '/admin/dashboard'});
                                            } else {
                                                navigate({
                                                    to: `/dashboard/${enrollment.guardianId}`,
                                                });
                                            }
                                        } else if (submitType === formSubmit) {
                                            submittedEnrollment(returnedEnrollment as Enrollment, {
                                                onSuccess: () => {
                                                    if (authenticatedUser !== null) {
                                                        navigate({to: '/admin/dashboard'});
                                                    } else {
                                                        navigate({
                                                            to: `/dashboard/${enrollment.guardianId}`,
                                                        });
                                                    }
                                                },
                                                onError: error => {
                                                    console.error('submit error', error);
                                                    setErrorMessage('saveError');
                                                    setHasError(true);
                                                },
                                            });
                                        }
                                    },
                                    onError: handleSaveError,
                                },
                            );
                        }),
                );
            }
        },
        [
            isStale,
            handleSaveError,
            enrollment,
            updateEnrollment,
            userStep,
            queryClient,
            navigate,
            formSchema,
            authenticatedUser,
            submittedEnrollment,
            castWithoutValidation,
        ],
    );

    const removeAttachmentFromParentForm = async (documentId: string, step: string) => {
        setErrorMessage('');
        setHasError(false);
        setIsProcessing(true);

        if (documentId && formRef.current) {
            deleteDocument(documentId, {
                onSuccess: (response: DeleteDocumentResponse) => {
                    // update the current form values
                    const newFormValues = {...formRef.current?.getValues()};

                    // The endpoint returns only the document that was deleted, so swap
                    // it in and leave the step's other documents in place. If the step
                    // never held an array, fall back to just the returned document.
                    const currentDocuments = newFormValues[`documents${step}`];
                    const updatedDocuments = Array.isArray(currentDocuments)
                        ? (currentDocuments as Document[]).map((document: Document) =>
                              document.id === documentId ? response.data : document,
                          )
                        : [response.data];

                    newFormValues[`documents${step}`] = updatedDocuments;

                    // shouldValidate re-runs the resolver so the card that just lost its
                    // file errors again, rather than holding the pre-delete state.
                    formRef.current?.setValue(`documents${step}`, updatedDocuments, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                    });

                    // update the query cache, clear the fileId and name
                    const newEnrollment = {...enrollment};
                    const newDocuments = [...(newEnrollment.documents || [])];
                    newEnrollment.documents = newDocuments.map((document: Document) => {
                        if (document.id === response.data.id) {
                            document.fileId = undefined;
                            document.name = undefined;
                        }

                        return document;
                    });

                    queryClient.setQueryData(enrollmentQueryKey(enrollmentId ?? ''), newEnrollment);
                    setIsProcessing(false);

                    handleFormSave(newFormValues);
                },
                onError: () => {
                    setErrorMessage(t('errorRemoveAttachment'));
                    setHasError(true);
                    setIsProcessing(false);
                },
            });
        } else {
            setErrorMessage(t('errorDocumentId'));
            setHasError(true);
        }
    };

    useEffect(() => {
        if (userStep !== undefined) {
            window.scrollTo(0, 0);
        }
    }, [userStep]);

    if (isEnrollmentLoading) {
        return <div>Loading ...</div>;
    }

    if (isEnrollmentError || enrollment === undefined) {
        const error = enrollmentError as MutateError;
        return <div>There has been an error fetching this enrollment. {error.message}</div>;
    }

    const formContext: FormContextInterface = {
        enrollment,
        formRef,
        formSchema,
        enrollmentSchema,
        userStep,
        setUserStep,
        handleFormSave,
        removeAttachmentFromParentForm,
        validatePage,
        validateEnrollment,
        isProcessing,
        isSaving,
        isSubmitting,
        isStale,
    };

    return (
        <>
            <FormControlContext.Provider value={formContext}>
                {children}
            </FormControlContext.Provider>
            <ErrorModal
                show={hasError}
                handleClose={() => {
                    setHasError(false);

                    if (leaveOnErrorClose) {
                        setLeaveOnErrorClose(false);
                        navigate({to: `/dashboard/${enrollment.guardianId}`});
                    }
                }}
                errorMessage={errorMessage}
            />
            <StaleEnrollmentModal
                show={isStale}
                handleReload={() => {
                    window.location.reload();
                }}
                handleKeepMine={keepThisTabsVersion}
            />
        </>
    );
};

const useFormControlContext = (): FormContextInterface => {
    const context = useContext(FormControlContext);

    if (Object.keys(context).length === 0) {
        throw new Error('useFormControlContext must be used within a FormControlContextProvider');
    }

    return context;
};

export {FormControlContextProvider, FormControlContext, useFormControlContext};
